/**
 * Phase 735 — AgendaTemplateEditor
 *
 * Rich text editor used exclusively inside the Agenda Templates editor
 * (`/pages/AgendaTemplates.js`).
 *
 * Features requested:
 *   • Bold / Italic / Underline / Bullet List / Numbered List / Alignment
 *   • Undo / Redo
 *   • "Insert Variable ▼" dropdown — inserts {placeholder} at caret
 *   • "Insert Snippet ▼" dropdown — inserts a full block at caret
 *   • Auto-resizes with parent (min-height + flex)
 *
 * The editor stores its body as HTML. The parent page uses Quill's
 * `getText()` to also extract a plain-text version so that the existing
 * MeetingsTab pipeline (which reads `greek_template_text` verbatim) keeps
 * working without modification.
 */
import React, { useMemo, useRef, useEffect, useId, forwardRef, useImperativeHandle } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Variable groups shown inside the Insert Variable dropdown. The strings on
// the right are the tokens that get inserted at the caret and later replaced
// by the backend PDF renderer with live meeting data.
export const AGENDA_VARIABLES = [
  {
    label: 'Εταιρεία / Company',
    items: [
      ['{company_name}', 'Επωνυμία εταιρείας'],
      ['{company_email}', 'Email εταιρείας'],
      ['{company_phone}', 'Τηλέφωνο εταιρείας'],
    ],
  },
  {
    label: 'Πολυκατοικία / Building',
    items: [
      ['{building_name}', 'Ονομασία πολυκατοικίας'],
    ],
  },
  {
    label: 'Συνέλευση / Meeting',
    items: [
      ['{meeting_title}', 'Τίτλος συνέλευσης'],
      ['{meeting_date}', 'Ημερομηνία'],
      ['{meeting_start_time}', 'Ώρα έναρξης'],
      ['{meeting_end_time}', 'Ώρα λήξης'],
      ['{meeting_location}', 'Τόπος'],
    ],
  },
  {
    label: 'Πρόεδρος / Chairman',
    items: [
      ['{chairman_name}', 'Όνομα'],
      ['{chairman_apartment}', 'Διαμέρισμα'],
      ['{chairman_phone}', 'Τηλέφωνο'],
    ],
  },
  {
    label: 'Γραμματέας / Secretary',
    items: [
      ['{secretary_name}', 'Όνομα'],
      ['{secretary_apartment}', 'Διαμέρισμα'],
      ['{secretary_phone}', 'Τηλέφωνο'],
    ],
  },
  {
    label: 'Ταμίας / Treasurer',
    items: [
      ['{treasurer_name}', 'Όνομα'],
      ['{treasurer_apartment}', 'Διαμέρισμα'],
      ['{treasurer_phone}', 'Τηλέφωνο'],
    ],
  },
];

// Ready-made blocks the user can drop into any template. Each snippet
// combines free-flowing Greek text with variables — the same replacement
// happens at PDF time, so a snippet always reads correctly regardless of
// meeting.
export const AGENDA_SNIPPETS = [
  {
    key: 'committee_block',
    label: 'Διαχειριστικό Συμβούλιο',
    // Uses HTML so the block already carries structure inside the editor.
    html:
      '<p>Εξελέγη το ακόλουθο Διαχειριστικό Συμβούλιο:</p>' +
      '<p><strong>Πρόεδρος:</strong> {chairman_name} — Διαμέρισμα {chairman_apartment} — Τηλ. {chairman_phone}</p>' +
      '<p><strong>Γραμματέας:</strong> {secretary_name} — Διαμέρισμα {secretary_apartment} — Τηλ. {secretary_phone}</p>' +
      '<p><strong>Ταμίας:</strong> {treasurer_name} — Διαμέρισμα {treasurer_apartment} — Τηλ. {treasurer_phone}</p>',
  },
  {
    key: 'quorum_paragraph',
    label: 'Παράγραφος Απαρτίας',
    html:
      '<p>Η Γενική Συνέλευση της πολυκατοικίας «{building_name}» συνήλθε ' +
      'νομίμως και σε απαρτία, σύμφωνα με τον Κανονισμό, με ποσοστό ' +
      'παρουσιάσεως που υπερβαίνει το προβλεπόμενο όριο επί των χιλιοστών ιδιοκτησίας.</p>',
  },
  {
    key: 'attendance_paragraph',
    label: 'Παράγραφος Παρουσίας',
    html:
      '<p>Η συνεδρίαση «{meeting_title}» πραγματοποιήθηκε την {meeting_date}, ' +
      'ώρα έναρξης {meeting_start_time}, στον χώρο {meeting_location}. ' +
      'Καταγράφηκαν οι παρόντες συνιδιοκτήτες βάσει του παρουσιολογίου που ακολουθεί.</p>',
  },
  {
    key: 'closing_paragraph',
    label: 'Επίλογος / Κλείσιμο',
    html:
      '<p>Μη υπάρχοντος άλλου θέματος προς συζήτηση, ο Πρόεδρος κ. ' +
      '{chairman_name} κήρυξε τη λήξη της Γενικής Συνέλευσης στις ' +
      '{meeting_end_time}, ευχαριστώντας τους παρόντες για τη συμμετοχή τους.</p>',
  },
  {
    key: 'signature_block',
    label: 'Υπογραφές',
    html:
      '<p>&nbsp;</p>' +
      '<p>____________________________</p><p>{chairman_name}<br/><em>Πρόεδρος</em></p>' +
      '<p>&nbsp;</p>' +
      '<p>____________________________</p><p>{secretary_name}<br/><em>Γραμματέας</em></p>' +
      '<p>&nbsp;</p>' +
      '<p>____________________________</p><p>{treasurer_name}<br/><em>Ταμίας</em></p>',
  },
];

const AgendaTemplateEditor = forwardRef(function AgendaTemplateEditor(
  { value = '', onChange, placeholder = '', minHeight = '260px', testId = 'agenda-tmpl-editor' },
  ref
) {
  const reactId = useId();
  const toolbarId = useMemo(
    () => `agenda-tmpl-toolbar-${reactId.replace(/:/g, '')}`,
    [reactId]
  );
  const quillRef = useRef(null);
  const toolbarRef = useRef(null);

  // Expose plain-text extraction to the parent so it can persist a
  // backward-compatible plain-text mirror alongside the HTML.
  useImperativeHandle(ref, () => ({
    getPlainText: () => {
      try {
        const q = quillRef.current?.getEditor?.();
        return (q?.getText?.() || '').replace(/\n+$/, '');
      } catch (_e) {
        return '';
      }
    },
    focus: () => {
      try { quillRef.current?.getEditor?.()?.focus?.(); } catch (_e) {/* noop */}
    },
  }), []);

  // Same defensive pattern used in RichEmailEditor — every toolbar
  // <button> must be type="button" or shadcn Dialogs treat clicks as
  // form submits and eat the toggle.
  useEffect(() => {
    const tb = toolbarRef.current;
    if (!tb) return;
    tb.querySelectorAll('button').forEach((b) => {
      if (!b.getAttribute('type')) b.setAttribute('type', 'button');
    });
  }, [toolbarId]);

  // Stretch the editor to the caller's requested min-height.
  useEffect(() => {
    const el = quillRef.current?.getEditor?.()?.root;
    if (el) el.style.minHeight = minHeight;
  }, [minHeight]);

  const modules = useMemo(() => {
    const safeRange = (quill) => {
      try {
        const r = quill.getSelection(true);
        if (r) return r;
        const len = quill.getLength();
        quill.setSelection(Math.max(0, len - 1), 0, 'silent');
        return quill.getSelection(true);
      } catch (_e) { return null; }
    };

    const inlineToggle = (name) => function () {
      try {
        const quill = this.quill; if (!quill) return;
        const range = safeRange(quill); if (!range) return;
        const cur = quill.getFormat(range);
        quill.format(name, !cur[name], 'user');
      } catch (e) { console.warn(`[AgendaTemplateEditor] ${name} failed`, e); }
    };

    const valueFormat = (name) => function (value) {
      try {
        const quill = this.quill; if (!quill) return;
        const range = safeRange(quill); if (!range) return;
        quill.format(name, value || false, 'user');
      } catch (e) { console.warn(`[AgendaTemplateEditor] ${name} apply failed`, e); }
    };

    return {
      toolbar: {
        container: `#${toolbarId}`,
        handlers: {
          bold: inlineToggle('bold'),
          italic: inlineToggle('italic'),
          underline: inlineToggle('underline'),
          align: valueFormat('align'),
          list: function (value) {
            try {
              const quill = this.quill; if (!quill) return;
              const range = safeRange(quill); if (!range) return;
              const cur = quill.getFormat(range);
              quill.format('list', cur.list === value ? false : value, 'user');
            } catch (e) { console.warn('[AgendaTemplateEditor] list failed', e); }
          },
        },
      },
      history: { delay: 500, maxStack: 200, userOnly: true },
    };
  }, [toolbarId]);

  const formats = ['bold', 'italic', 'underline', 'list', 'align'];

  // Insert a plain-text token (e.g. "{chairman_name}") at the caret.
  const insertToken = (token) => {
    try {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;
      let range = quill.getSelection(true);
      if (!range) {
        const len = quill.getLength();
        quill.setSelection(Math.max(0, len - 1), 0, 'silent');
        range = quill.getSelection(true);
      }
      const idx = range ? range.index : quill.getLength();
      quill.insertText(idx, token, 'user');
      quill.setSelection(idx + token.length, 0, 'silent');
    } catch (e) { console.warn('[AgendaTemplateEditor] insertToken failed', e); }
  };

  // Insert an entire HTML snippet at the caret. `dangerouslyPasteHTML`
  // is Quill's official API for inserting arbitrary HTML while keeping
  // the delta / history stack consistent.
  const insertSnippet = (html) => {
    try {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;
      let range = quill.getSelection(true);
      if (!range) {
        const len = quill.getLength();
        quill.setSelection(Math.max(0, len - 1), 0, 'silent');
        range = quill.getSelection(true);
      }
      const idx = range ? range.index : quill.getLength();
      quill.clipboard.dangerouslyPasteHTML(idx, html, 'user');
    } catch (e) { console.warn('[AgendaTemplateEditor] insertSnippet failed', e); }
  };

  const onVariableSelect = (e) => {
    const token = e.target.value;
    e.target.value = ''; // reset so same value can be re-selected
    if (token) insertToken(token);
  };

  const onSnippetSelect = (e) => {
    const key = e.target.value;
    e.target.value = '';
    const snip = AGENDA_SNIPPETS.find((s) => s.key === key);
    if (snip) insertSnippet(snip.html);
  };

  return (
    <div className="agenda-tmpl-editor" data-testid={testId}>
      {/* Insert Variable + Insert Snippet — sit ABOVE the standard toolbar
          so they read naturally left-to-right. */}
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <select
          onChange={onVariableSelect}
          defaultValue=""
          className="text-xs border border-zinc-300 bg-white px-2 py-1 rounded-none"
          data-testid={`${testId}-insert-variable`}
        >
          <option value="">Insert Variable ▼</option>
          {AGENDA_VARIABLES.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.items.map(([token, desc]) => (
                <option key={token} value={token}>{token} — {desc}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <select
          onChange={onSnippetSelect}
          defaultValue=""
          className="text-xs border border-zinc-300 bg-white px-2 py-1 rounded-none"
          data-testid={`${testId}-insert-snippet`}
        >
          <option value="">Insert Snippet ▼</option>
          {AGENDA_SNIPPETS.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Standard Quill toolbar — kept compact per the feature list. */}
      <div ref={toolbarRef} id={toolbarId} className="ql-toolbar ql-snow">
        <span className="ql-formats">
          <button type="button" className="ql-bold" data-testid={`${testId}-bold`} />
          <button type="button" className="ql-italic" data-testid={`${testId}-italic`} />
          <button type="button" className="ql-underline" data-testid={`${testId}-underline`} />
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-list" value="bullet" data-testid={`${testId}-bullet`} />
          <button type="button" className="ql-list" value="ordered" data-testid={`${testId}-ordered`} />
        </span>
        <span className="ql-formats">
          <select className="ql-align" data-testid={`${testId}-align`} />
        </span>
        <span className="ql-formats">
          <button
            type="button"
            onClick={() => { try { quillRef.current?.getEditor?.()?.history?.undo?.(); } catch (e) {/* noop */} }}
            title="Undo"
            data-testid={`${testId}-undo`}
            style={{ width: 28 }}
          >
            <svg viewBox="0 0 18 18">
              <polygon className="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10" />
              <path className="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => { try { quillRef.current?.getEditor?.()?.history?.redo?.(); } catch (e) {/* noop */} }}
            title="Redo"
            data-testid={`${testId}-redo`}
            style={{ width: 28 }}
          >
            <svg viewBox="0 0 18 18">
              <polygon className="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10" />
              <path className="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5" />
            </svg>
          </button>
        </span>
      </div>

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={(html) => onChange && onChange(html)}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />

      <style>{`
        .agenda-tmpl-editor .ql-container.ql-snow { border-radius: 0; font-size: 14px; font-family: Georgia, serif; }
        .agenda-tmpl-editor .ql-toolbar.ql-snow { border-radius: 0; border-bottom: none; background: #fafafa; }
        .agenda-tmpl-editor .ql-editor { min-height: ${minHeight}; padding: 12px 14px; }
        .agenda-tmpl-editor .ql-editor p { margin: 0 0 6px 0; }
      `}</style>
    </div>
  );
});

export default AgendaTemplateEditor;

// Sample data used inside the Preview tab of AgendaTemplates.js so the
// user can see exactly how variables will resolve at PDF time.
export const AGENDA_SAMPLE_VARS = {
  '{company_name}': 'Cyprus Property Management LTD',
  '{company_email}': 'info@cpm.com.cy',
  '{company_phone}': '+357 22 000000',
  '{building_name}': 'Test Building',
  '{meeting_title}': 'Έκτακτη Γενική Συνέλευση',
  '{meeting_date}': '15/03/2026',
  '{meeting_start_time}': '19:00',
  '{meeting_end_time}': '20:45',
  '{meeting_location}': 'Χώρος Πολυκατοικίας',
  '{chairman_name}': 'Γιάννης Παπαδόπουλος',
  '{chairman_apartment}': '101',
  '{chairman_phone}': '99123456',
  '{secretary_name}': 'Μαρία Ιωαννίδη',
  '{secretary_apartment}': '202',
  '{secretary_phone}': '99234567',
  '{treasurer_name}': 'Κώστας Χριστοφή',
  '{treasurer_apartment}': '303',
  '{treasurer_phone}': '99345678',
};

// Utility used by the Preview tab. Replaces every {token} in `html` with
// the value from `vars`. Tokens not in the map are left unchanged.
export function resolveAgendaVars(html, vars = AGENDA_SAMPLE_VARS) {
  if (!html) return '';
  return String(html).replace(/\{[a-z_]+\}/gi, (m) => (m in vars ? vars[m] : m));
}
